import { Task } from '@mui/icons-material';
import { Box, Card, CardActionArea, CardHeader, Stack, Typography } from '@mui/material';
import { blue, green, grey, orange, red } from '@mui/material/colors';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { PersonReadDTO } from '../../api/dto';
import { formatDateTime } from '../../lib/date';

export enum TaskColor {
    ACTIVE_SELECTED,
    ACTIVE_UNSELECTED,
    ACTIVE_UNSELECTABLE,
    COMPLETED_POSITIVE,
    COMPLETED_NEGATIVE,
    COMPLETED_NEUTRAL,
    CANCELLED,
}

// TODO: these should instead come from the theme
const TaskColorMapping: Record<TaskColor, string> = {
    [TaskColor.ACTIVE_SELECTED]: blue[50],
    [TaskColor.ACTIVE_UNSELECTED]: 'white',
    [TaskColor.ACTIVE_UNSELECTABLE]: grey[100],
    [TaskColor.COMPLETED_POSITIVE]: green[50],
    [TaskColor.COMPLETED_NEGATIVE]: red[50],
    [TaskColor.COMPLETED_NEUTRAL]: orange[50],
    [TaskColor.CANCELLED]: grey[300],
};

interface CustomCardActionAreaProps {
    onClick: () => void;
    readOnly: boolean;
    disabled: boolean;
    children: React.ReactNode;
}

// CardActionArea['disabled'] is not good because it also disables the content
const CustomCardActionArea: React.FC<CustomCardActionAreaProps> = ({ onClick, readOnly, disabled, children }) => {
    return !readOnly ? (
        <CardActionArea disabled={disabled} onClick={() => onClick()}>
            {children}
        </CardActionArea>
    ) : (
        <>{children}</>
    );
};

interface TaskCardLayoutProps {
    color: TaskColor;
    icon?: React.ReactNode;
    selected?: boolean;
    readOnly?: boolean;
    disabled?: boolean;
    dashed?: boolean;
    type: string;
    role: string | [string, ...string[]];
    status: (
        | {
              active: true;
              assignees: Pick<PersonReadDTO, 'searchLabel'>[];
          }
        | {
              active: false;
              executor: Pick<PersonReadDTO, 'searchLabel'> | null;
              date: Date;
          }
    ) & {
        assignees: Pick<PersonReadDTO, 'searchLabel'>[];
    };
    instruction?: string;
    action?: React.ReactNode;
    onCardClick?: () => void;
    mandatory?: boolean;
}

export const TaskCardLayout: React.FC<TaskCardLayoutProps> = ({
    color: colorType,
    icon,
    selected,
    readOnly,
    disabled,
    dashed,
    type,
    role: roleRaw,
    status,
    instruction,
    action,
    onCardClick,
    mandatory,
}) => {
    const intl = useIntl();
    const color = TaskColorMapping[colorType];
    const handleCardClick = useCallback(() => onCardClick && onCardClick(), [onCardClick]);
    const role = useMemo(() => (typeof roleRaw === 'string' ? [roleRaw] : roleRaw), [roleRaw]);
    return (
        <Card
            variant={selected ? undefined : 'outlined'}
            sx={{
                background: color,
                borderStyle: !selected && dashed ? 'dashed' : undefined,
            }}
        >
            <CustomCardActionArea onClick={handleCardClick} readOnly={!!readOnly} disabled={!!disabled}>
                <CardHeader
                    avatar={
                        <Box color={colorType === TaskColor.ACTIVE_SELECTED ? blue[500] : 'rgba(0, 0, 0, 0.54)'}>
                            {icon ?? <Task />}
                        </Box>
                    }
                    action={
                        action ? (
                            <Stack
                                onClick={(e) => e.stopPropagation()}
                                onTouchStart={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                direction="column"
                                justifyContent="space-between"
                                spacing={0.5}
                            >
                                {/* This `<Stack/>` prevent the propagation of unrelated events to `<CardActionArea/>` */}
                                {action}
                            </Stack>
                        ) : undefined
                    }
                    title={<Box sx={{ fontWeight: 'medium' }}>{type}</Box>}
                    subheader={
                        <>
                            <Stack direction="column">
                                {role.map((line, i) => (
                                    <Typography key={i} variant="body2" fontSize={12} fontWeight="medium">
                                        {line}
                                    </Typography>
                                ))}
                            </Stack>
                            {mandatory && status.assignees.length === 0 ? (
                                <Typography variant="body2" fontSize={10} color="error" fontWeight={700}>
                                    <FormattedMessage id="document.signature.mandatory.noAssignees" />
                                </Typography>
                            ) : (
                                <Typography variant="body2" fontSize={10}>
                                    {status.active || status.executor === null
                                        ? status.assignees.length === 1
                                            ? status.assignees[0].searchLabel
                                            : intl.formatMessage(
                                                  { id: 'document.task.assignees.count' },
                                                  {
                                                      person: status.assignees.length,
                                                  }
                                              )
                                        : intl.formatMessage(
                                              { id: 'document.task.performedBy' },
                                              {
                                                  person: status.executor.searchLabel,
                                                  date: formatDateTime(status.date!, intl),
                                              }
                                          )}
                                    {!status.active && status.executor === null && (
                                        <Typography variant="inherit">
                                            <FormattedMessage id="document.task.notPerformed" />
                                        </Typography>
                                    )}
                                </Typography>
                            )}
                            {!!instruction && (
                                <Typography fontSize={14} sx={{ mt: 1 }}>
                                    {instruction}
                                </Typography>
                            )}
                        </>
                    }
                />
                {/* TODO list of steps to complete the task inside the card content or a collapsible panel */}
            </CustomCardActionArea>
        </Card>
    );
};
